import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApplications } from '../../api/applications';
import { useJob, useUpdateJob } from '../../api/jobs';
import ApplicationTable from '../../components/ApplicationTable';
import ReviewersManager from '../../components/ReviewersManager';
import JobForm from './JobForm';

const STATUS_CLASS: Record<string, string> = {
  DRAFT: 'badge', OPEN: 'badge badge-open', CLOSED: 'badge badge-closed',
};

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: job, isLoading, error } = useJob(id);
  const { data: applications } = useApplications(id);
  const update = useUpdateJob(id!);
  const [editing, setEditing] = useState(false);

  if (isLoading) return <p className="muted">Loading job…</p>;
  if (error) return <p className="error">Could not load job: {String(error)}</p>;
  if (!job) return null;

  if (editing) {
    return (
      <div className="panel">
        <h2>Edit job description</h2>
        <JobForm
          initial={job}
          submitLabel="Save changes"
          pending={update.isPending}
          error={update.error instanceof Error ? update.error.message : null}
          onCancel={() => setEditing(false)}
          onSubmit={(values) => update.mutate(values, { onSuccess: () => setEditing(false) })}
        />
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <div>
          <Link to="/jobs" className="muted small">← All jobs</Link>
          <h2 style={{ marginTop: '0.4rem' }}>{job.title}</h2>
        </div>
        {job.canEdit && <button className="btn-ghost" onClick={() => setEditing(true)}>Edit</button>}
      </div>

      <div className="meta-grid">
        <div><span className="meta-label">Status</span><span className={STATUS_CLASS[job.status]}>{job.status}</span></div>
        <div><span className="meta-label">Department</span><span>{job.department ?? '—'}</span></div>
        <div><span className="meta-label">Location</span><span>{job.location ?? '—'}</span></div>
        <div><span className="meta-label">Approval threshold</span><span>{Math.round(job.reviewThreshold * 100)}%</span></div>
        <div><span className="meta-label">Candidates</span><span>{job.candidateCount}</span></div>
        <div><span className="meta-label">Created by</span><span>{job.createdBy.displayName}</span></div>
      </div>

      <h3>Description</h3>
      <p className="description">{job.description}</p>

      {job.canEdit && (
        <>
          <h3 style={{ marginTop: '1.75rem' }}>Reviewers</h3>
          <p className="muted small">
            Reviewers vote on every candidate for this job. HR advances once the approval threshold
            ({Math.round(job.reviewThreshold * 100)}%) is met.
          </p>
          <ReviewersManager jobId={job.id} />
        </>
      )}

      <div className="section-head">
        <h3>Candidates</h3>
        {job.canEdit && (
          <button className="btn-primary" onClick={() => navigate(`/jobs/${job.id}/propose`)}>Propose candidate</button>
        )}
      </div>
      <ApplicationTable applications={applications ?? []} />
    </div>
  );
}
